import { Chronotype, DefaultEventCategoryId, EnergyFluctuationItem } from '@demind-inc/core';

import { EnergyFluctuateItemSet } from './types';

import { upperFirst } from 'lodash';
import { getDefaultCategoryEventIconSet } from '../../../../common';
import { formatToAmPm, formatToHHmm } from '../../../../../data-access';
import { getCssVariable } from '../../../../../utils';
import { ReactComponent as MoonIcon } from '../../../../../assets/actions/moon.svg';
import { ReactComponent as EmaIcon } from '../../../../../assets/calendar/category/ema.svg';
import { DataTFunction, getCircadianPhaseTitle } from '../../../i18n';

const whiteColor = getCssVariable('--color-white-lightest');

export const transformSleepFluctuationItem = (
  item: EnergyFluctuationItem,
  chronotype?: Chronotype,
  format24h = false,
  t?: DataTFunction
): EnergyFluctuateItemSet => {
  const sleepTitle = t?.('data.sleep.title') ?? 'Sleep';
  const chronotypeTitle = chronotype
    ? (t?.(`data.sleep.chronotypes.${chronotype}`) ?? chronotype)
    : null;

  return {
    icon: <MoonIcon color={whiteColor} />,
    title:
      chronotypeTitle && t
        ? t('data.circadian.fluctuations.sleepWithChronotype', {
            chronotype: chronotypeTitle,
          })
        : sleepTitle,
    eventTime: `${
      format24h ? formatToHHmm(item.eventTime.startTime) : formatToAmPm(item.eventTime.startTime)
    } - ${
      format24h ? formatToHHmm(item.eventTime.endTime!) : formatToAmPm(item.eventTime.endTime!)
    }`,
    reason: 'sleep',
    fluctuateValue: item.fluctuateScore * 100,
    fluctuateTime: t?.('data.circadian.peakEnergy') ?? 'Peak Energy',
  } as EnergyFluctuateItemSet;
};

export const transformPhaseEmaFluctuationItem = (
  item: EnergyFluctuationItem,
  format24h = false,
  t?: DataTFunction
): EnergyFluctuateItemSet => {
  const phaseTitle = t
    ? getCircadianPhaseTitle(t, item.eventTitle, upperFirst(item.eventTitle))
    : upperFirst(item.eventTitle);

  return {
    icon: <EmaIcon height={18} />,
    title:
      t?.('data.satisfaction.subjectiveEnergyWithContext', {
        context: phaseTitle,
      }) ?? `Subjective Energy (${phaseTitle})`,
    reason: 'phaseEma',
    eventTime: `${
      format24h ? formatToHHmm(item.eventTime.startTime) : formatToAmPm(item.eventTime.startTime)
    }`,
    fluctuateValue: item.fluctuateScore,
    fluctuateTime:
      !!item.fluctuateTime?.startTime && !!item.fluctuateTime?.endTime
        ? `${
            format24h
              ? formatToHHmm(item.fluctuateTime?.startTime!)
              : formatToAmPm(item.fluctuateTime?.startTime!)
          } - ${
            format24h
              ? formatToHHmm(item.fluctuateTime?.endTime!)
              : formatToAmPm(item.fluctuateTime?.endTime!)
          }`
        : '',
  };
};

export const transformEventEmaFluctuationItem = (
  item: EnergyFluctuationItem,
  format24h = false
): EnergyFluctuateItemSet => {
  return {
    icon: getDefaultCategoryEventIconSet(
      (item.eventCategory?.id as DefaultEventCategoryId) ?? 'default_other'
    ),
    title: item.eventTitle,
    eventTime: `${
      format24h ? formatToHHmm(item.eventTime.startTime) : formatToAmPm(item.eventTime.startTime)
    } - ${
      format24h ? formatToHHmm(item.eventTime.endTime!) : formatToAmPm(item.eventTime.endTime!)
    }`,
    reason: 'event',
    fluctuateValue: item.fluctuateScore,
    fluctuateTime: `${
      format24h
        ? formatToHHmm(item.fluctuateTime?.startTime!)
        : formatToAmPm(item.fluctuateTime?.startTime!)
    } - ${
      format24h
        ? formatToHHmm(item.fluctuateTime?.endTime!)
        : formatToAmPm(item.fluctuateTime?.endTime!)
    }`,
    eventCategory: item.eventCategory,
  };
};
